import React, { useCallback, useEffect, useState, useMemo, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import Form, { ButtonItem, SimpleItem, EmptyItem, GroupItem, ColCountByScreen, Label } from 'devextreme-react/form'
import 'devextreme-react/tag-box'
import JumboCardQuick from '@jumbo/components/JumboCardQuick'
import Typography from '@mui/material/Typography'
import { useDispatch, useSelector } from 'react-redux'
import DataSource from 'devextreme/data/data_source'
import classesService from 'app/services/pages/courses/setup/classesServices/classesService'
import utilServices from 'app/services/util-services'
import CustomStore from 'devextreme/data/custom_store'
import { useSnackbar } from 'notistack'
import { IconButton, Stack, useMediaQuery } from '@mui/material'
import { updateSection } from 'app/redux/store/features/sections'
import { updateIndicator } from 'app/redux/store/features/configs'
import WestIcon from '@mui/icons-material/West'
import { createTeacherLookupCustomStore } from 'app/utils/teacherLookupCustomStore/teacherLookupCustomStore'
import { DropDownButtonRender } from 'app/utils/DropDownButtonRender/DropDownButtonRender'
import { SelectBox } from 'devextreme-react'

const UpdateClassSectionForm = (props) => {
    const navigate = useNavigate()
    const formRef = useRef(null)
    const dispatch = useDispatch()
    const { enqueueSnackbar } = useSnackbar()
    const { termConfig } = useSelector((state) => state.terms)
    const { loading } = useSelector((state) => state.sections)
    const [labelLocation, setLabelLocation] = useState('left')
    const sm = useMediaQuery('(max-width:600px)')
    const [classId, setClassId] = useState(props.sectionData.classId)
    const [teacherId, setTeacherId] = useState(props.sectionData.teacherId)
    const [selectBoxLoading, setSelectBoxLoading] = useState({
        classNameLoading: false,
    })
    const [showValidationError, setShowValidationError] = useState(false)

    useEffect(() => {
        setLabelLocation(sm ? 'top' : 'left')
    }, [sm])

    useEffect(() => {
        setClassId(props.sectionData.classId)
        setTeacherId(props.sectionData.teacherId)
    }, [props.sectionData])

    function handleError(error) {
        if (error.status === '401') {
            navigate('/profile/signout')
        } else if (error.detail) {
            enqueueSnackbar(error.detail, {
                variant: 'error',
                anchorOrigin: {
                    vertical: 'top',
                    horizontal: 'right',
                },
            })
        } else {
            enqueueSnackbar(error, {
                variant: 'error',
                anchorOrigin: {
                    vertical: 'top',
                    horizontal: 'right',
                },
            })
        }
    }

    const handleErrorRef = useRef(handleError)
    handleErrorRef.current = handleError

    const classLookupList = useCallback(
        async (searchTerm) => {
            try {
                return await classesService.getClassLookupList({ termId: termConfig.termId, className: searchTerm })
            } catch (error) {
                handleError(error)
            }
        },
        [termConfig.termId]
    )

    const classLookupsStore = useMemo(
        () =>
            new DataSource({
                store: new CustomStore({
                    key: 'id',
                    load: async (loadOptions) => {
                        const searchTerm = loadOptions.searchValue || ''
                        return await classLookupList(searchTerm)
                    },
                    byKey: async (key) => {
                        if (key === '') return utilServices.emptyList()

                        setSelectBoxLoading((prev) => ({ ...prev, classNameLoading: true }))

                        try {
                            return await await classesService.getClassLookupById(key)
                        } catch (error) {
                            handleError(error)
                        } finally {
                            setSelectBoxLoading((prev) => ({ ...prev, classNameLoading: false }))
                        }
                    },
                    errorHandler: (e) => console.error(e),
                }),
                reshapeOnPush: true,
            }),
        []
    )

    const onClassNameValueChanged = useCallback((e) => {
        setClassId(e.value)
    }, [])

    const onTeacherValueChanged = useCallback((e) => {
        setTeacherId(e.value)
    }, [])

    const teacherFullName = useMemo(() => {
        return (data) => {
            return data ? data.firstName.toFullName(data.lastName) : ''
        }
    }, [])

    const buildPrimaryTeacherIncludeIds = useCallback((data) => {
        if (data?.teacherId == null || data.teacherId === '') return []
        return [String(data.teacherId)]
    }, [])

    const buildAdditionalTeacherIncludeIds = useCallback((data) => {
        if (!Array.isArray(data?.additionalTeacherIds)) return []
        return utilServices.sanitizeAdditionalTeacherIds(data, data.additionalTeacherIds)
    }, [])

    const teacherPrimaryIncludeIdsRef = useRef([])
    teacherPrimaryIncludeIdsRef.current = buildPrimaryTeacherIncludeIds(props.sectionData)

    const teacherAdditionalIncludeIdsRef = useRef([])
    teacherAdditionalIncludeIdsRef.current = buildAdditionalTeacherIncludeIds(props.sectionData)

    const teacherPrimarySignature = useMemo(
        () => `${props.sectionData?.id ?? ''}|${props.sectionData?.teacherId ?? ''}`,
        [props.sectionData?.id, props.sectionData?.teacherId]
    )

    const teacherAdditionalSignature = useMemo(() => {
        const d = props.sectionData
        const key = Array.isArray(d?.additionalTeacherIds)
            ? [...d.additionalTeacherIds].map(String).sort().join(',')
            : ''
        return `${d?.id ?? ''}|${key}`
    }, [props.sectionData?.id, props.sectionData?.additionalTeacherIds])

    const teacherPrimaryLoadCacheRef = useRef(new Map())
    const teacherPrimaryLoadInFlightRef = useRef(new Map())
    const teacherAdditionalLoadCacheRef = useRef(new Map())
    const teacherAdditionalLoadInFlightRef = useRef(new Map())

    const teacherPrimaryStoreRef = useRef(null)
    if (!teacherPrimaryStoreRef.current) {
        teacherPrimaryStoreRef.current = createTeacherLookupCustomStore({
            getIncludeIds: () => teacherPrimaryIncludeIdsRef.current,
            loadCacheRef: teacherPrimaryLoadCacheRef,
            loadInFlightRef: teacherPrimaryLoadInFlightRef,
            getHandleError: () => handleErrorRef.current,
        })
    }

    const teacherAdditionalStoreRef = useRef(null)
    if (!teacherAdditionalStoreRef.current) {
        teacherAdditionalStoreRef.current = createTeacherLookupCustomStore({
            getIncludeIds: () => teacherAdditionalIncludeIdsRef.current,
            loadCacheRef: teacherAdditionalLoadCacheRef,
            loadInFlightRef: teacherAdditionalLoadInFlightRef,
            getHandleError: () => handleErrorRef.current,
        })
    }

    const teacherLookupsStorePrimary = useMemo(
        () => new DataSource({ store: teacherPrimaryStoreRef.current, reshapeOnPush: true }),
        []
    )
    const teacherLookupsStoreTagBox = useMemo(
        () => new DataSource({ store: teacherAdditionalStoreRef.current, reshapeOnPush: true }),
        []
    )

    useEffect(() => {
        teacherPrimaryLoadCacheRef.current.clear()
        teacherPrimaryLoadInFlightRef.current.clear()
        teacherLookupsStorePrimary.reload()
    }, [teacherPrimarySignature, teacherLookupsStorePrimary])

    useEffect(() => {
        teacherAdditionalLoadCacheRef.current.clear()
        teacherAdditionalLoadInFlightRef.current.clear()
        teacherLookupsStoreTagBox.reload()
    }, [teacherAdditionalSignature, teacherLookupsStoreTagBox])

    const teacherEditorOptions = useMemo(
        () => ({
            dataSource: teacherLookupsStoreTagBox,
            valueExpr: 'id',
            searchEnabled: true,
            searchTimeout: 300,
            minSearchLength: 1,
            showDataBeforeSearch: true,
            showClearButton: true,
            hideSelectedItems: false,
            itemTemplate: teacherFullName,
            displayExpr: teacherFullName,
            showSelectionControls: true,
            maxDisplayedTags: 3,
            applyValueMode: 'instantly',
        }),
        [teacherLookupsStoreTagBox, teacherFullName]
    )

    const submitButtonOptions = {
        text: 'Save',
        type: 'default',
        width: 120,
        useSubmitBehavior: true,
    }

    const cancelButtonOptions = {
        text: 'Cancel',
        type: 'Normal',
        width: 120,
        onClick: () => navigate('/courses/setup/classes', { state: { navigateFrom: 'update-section' } }),
    }

    const validationRules = {
        sectionName: [{ type: 'required', message: 'Section Name is required.' }],
    }

    const onUpdateSection = useCallback(
        async (formData) => {
            if (!teacherId) {
                setShowValidationError(true)
                return
            }
            const formInstance = formRef.current?.instance
            const additionalEditor = formInstance?.getEditor?.('additionalTeacherIds')
            let additionalIds = Array.isArray(formData?.additionalTeacherIds) ? formData.additionalTeacherIds : []
            if (additionalEditor) {
                const val = additionalEditor.option('value')
                additionalIds = Array.isArray(val) ? val : []
            }
            const host = { ...formData, teacherId }
            const payload = {
                ...formData,
                teacherId,
                additionalTeacherIds: utilServices.sanitizeAdditionalTeacherIds(host, additionalIds),
            }
            props.setApiCallInProgress(true)
            dispatch(updateIndicator({ inProgress: true }))
            dispatch(updateSection(payload))
        },
        [teacherId, dispatch, props.setApiCallInProgress]
    )

    return (
        <React.Fragment>
            <JumboCardQuick
                title={
                    <Stack direction='row' alignItems='center' spacing={1}>
                        <IconButton
                            className='backArrowForm'
                            onClick={() =>
                                navigate(`/courses/setup/classes`, {
                                    state: { navigateFrom: 'update-section' },
                                })
                            }>
                            <WestIcon />
                        </IconButton>
                        <Typography color={'common.white'} variant={'h3'} sx={{ mt: 0.8 }}>
                            Update Section
                        </Typography>
                    </Stack>
                }
                headerSx={{
                    height: 40,
                    bgcolor: 'primary.main',
                }}>
                <form
                    onSubmit={(e) => {
                        e.preventDefault()
                        const data = formRef.current?.instance?.option('formData')
                        onUpdateSection(data ?? props.sectionData)
                    }}>
                    <Form
                        ref={formRef}
                        mode='form'
                        formData={props.sectionData}
                        showColonAfterLabel={false}
                        showValidationSummary={false}
                        labelLocation={labelLocation}>
                        <GroupItem caption='Update Section' cssClass='form-group-item'>
                            <ColCountByScreen lg={13} md={13} sm={1} xs={1} />
                            <GroupItem />
                            <GroupItem />
                            <GroupItem />
                            <GroupItem cssClass='form-group-item' colSpan={6}>
                                <EmptyItem />
                                <SimpleItem>
                                    <Label text='Class Name' />
                                    <SelectBox
                                        disabled={true}
                                        dataSource={classLookupsStore}
                                        searchEnabled={true}
                                        showClearButton={true}
                                        defaultValue={props.sectionData.classId}
                                        value={classId}
                                        displayExpr='className'
                                        valueExpr='id'
                                        onValueChanged={onClassNameValueChanged}
                                        dropDownButtonRender={DropDownButtonRender(
                                            selectBoxLoading.classNameLoading
                                        )}></SelectBox>
                                </SimpleItem>
                                <SimpleItem dataField='sectionName' validationRules={validationRules.sectionName} />
                                <SimpleItem dataField='level' />
                                <SimpleItem
                                    dataField='capacity'
                                    editorType='dxNumberBox'
                                    editorOptions={{ showSpinButtons: 'true' }}
                                />
                                <SimpleItem isRequired={true}>
                                    <Label text='Primary teacher' />
                                    <SelectBox
                                        dataSource={teacherLookupsStorePrimary}
                                        valueExpr='id'
                                        searchEnabled={true}
                                        showClearButton={true}
                                        defaultValue={props.sectionData.teacherId}
                                        value={teacherId}
                                        displayExpr={teacherFullName}
                                        itemRender={teacherFullName}
                                        onValueChanged={onTeacherValueChanged}
                                        dropDownButtonRender={DropDownButtonRender(false)}></SelectBox>
                                    {showValidationError && !teacherId && (
                                        <div
                                            className='dx-invalid-message'
                                            style={{ color: '#d9534f', fontSize: '12px', marginTop: '4px' }}>
                                            Teacher Name is required.
                                        </div>
                                    )}
                                </SimpleItem>
                                <SimpleItem
                                    dataField='additionalTeacherIds'
                                    editorType='dxTagBox'
                                    editorOptions={teacherEditorOptions}>
                                    <Label text='Additional teachers' />
                                </SimpleItem>
                                <SimpleItem dataField='description' editorType='dxTextArea' />
                                <SimpleItem dataField='comments' editorType='dxTextArea' />
                            </GroupItem>
                        </GroupItem>
                        <EmptyItem />
                        <GroupItem>
                            <ColCountByScreen lg={2} md={2} sm={2} xs={2} />
                            <ButtonItem
                                buttonOptions={{
                                    disabled: loading,
                                    icon: loading ? 'refresh' : 'check',
                                    ...submitButtonOptions,
                                }}
                                horizontalAlignment='right'
                            />
                            <ButtonItem buttonOptions={cancelButtonOptions} horizontalAlignment='left' />
                        </GroupItem>
                    </Form>
                </form>
            </JumboCardQuick>
        </React.Fragment>
    )
}

export default UpdateClassSectionForm
